import { Flex, Switch, Text, ThemeIcon, Title } from '@mantine/core';
import {
  IconAlertTriangle,
  IconChecks,
  IconExclamationCircle,
} from '@tabler/icons-react';
import { CandidateInterviewChatMessageInput } from './components/CandidateInterviewChatMessageInput';
import { CandidateInterviewChatMessageList } from './components/CandidateInterviewChatMessageList';
import {
  getCandidateDisplayName,
  useGetCandidate,
  useUpdateCandidateInterview,
  type CandidateProcessRequestParams,
} from '@entities/candidates';
import { useInViewport } from '@mantine/hooks';
import { useParams } from 'react-router';
import { useCandidateInterviewChat } from './hooks/useCandidateInterviewChat';
import { useState, type ChangeEvent, useEffect, useMemo } from 'react';
import { showNotification } from '@shared/utils';

type CandidateInterviewProps = {
  resumeId?: number;
};

export const CandidateInterview = ({ resumeId }: CandidateInterviewProps) => {
  const { id } = useParams();
  const { data } = useGetCandidate(id || '');
  const processRequestParams = useMemo<CandidateProcessRequestParams>(
    () => ({
      filter: resumeId === undefined ? {} : { resumeId },
    }),
    [resumeId]
  );
  const { ref: chatViewportRef, inViewport } = useInViewport();
  const candidateName = getCandidateDisplayName({
    id: data?.id ?? id ?? '',
    firstName: data?.firstName,
    lastName: data?.lastName,
  });
  const chat = useCandidateInterviewChat(
    id || '',
    processRequestParams,
    inViewport,
    candidateName
  );
  const [isChecked, setIsChecked] = useState(!data?.interviewRequired || false);
  const { mutate: updateCandidateInterview, isPending } =
    useUpdateCandidateInterview(id || '');

  useEffect(() => {
    if (data?.interviewRequired !== undefined) {
      setIsChecked(!data.interviewRequired);
    }
  }, []);

  const handleChangeSwitch = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setIsChecked(checked);

    updateCandidateInterview(
      {},
      {
        onError: error => {
          setIsChecked(!checked);
          showNotification({
            error,
            variant: 'error',
            message:
              error.response?.data.message ||
              'Не удалось изменить статус кандидата',
          });
        },
      }
    );
  };

  return (
    <Flex w='100%' direction='column' gap={12}>
      <Flex justify='space-between' align='center'>
        <Flex gap={8}>
          {!isChecked ? (
            <ThemeIcon miw={36} mih={36} bg='red.0' radius={100}>
              <IconAlertTriangle size={24} color='var(--mantine-color-red-7)' />
            </ThemeIcon>
          ) : (
            <ThemeIcon miw={36} mih={36} bg='green.0' radius={100}>
              <IconChecks size={24} color='var(--mantine-color-green-7)' />
            </ThemeIcon>
          )}
          <Title order={4}>Интервью с кандидатом</Title>
        </Flex>
        <Switch
          checked={isChecked}
          onChange={event => handleChangeSwitch(event)}
          label='Участие Синтии'
          disabled={isPending}
        />
      </Flex>
      {!isChecked && (
        <Flex
          align='center'
          style={{ borderRadius: '6px' }}
          p={8}
          gap={6}
          bg='red.0'
        >
          <IconExclamationCircle color='#d22600' size={16} stroke={3} />
          <Text c='#d22600' size='xs'>
            Диалог Синтии с кандидатом приостановлен.
          </Text>
        </Flex>
      )}
      <Flex direction='column' gap={24}>
        <CandidateInterviewChatMessageList
          chat={chat}
          viewportRef={chatViewportRef}
        />
        <CandidateInterviewChatMessageInput
          sendMessage={chat.sendMessage}
          isSending={chat.isSending}
        />
      </Flex>
    </Flex>
  );
};
